import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Trash2, GripVertical, Upload, Film, X } from "lucide-react";
import { toast } from "sonner";
import { buildSeoFileName } from "@/data/galleryMedia";

type MediaItem = {
  id: string;
  type: string;
  url: string;
  poster_url: string | null;
  alt: string;
  mirror: boolean;
  position: number;
};

export default function GalleryManager() {
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [editingAlt, setEditingAlt] = useState<string | null>(null);

  const fetchMedia = useCallback(async () => {
    const { data } = await supabase
      .from("gallery_media")
      .select("*")
      .order("position", { ascending: true });
    if (data) setMedia(data);
  }, []);

  useEffect(() => { fetchMedia(); }, [fetchMedia]);

  const uploadFiles = async (files: FileList | File[]) => {
    setUploading(true);
    const fileArr = Array.from(files);
    const maxPosition = media.length > 0 ? Math.max(...media.map((m) => m.position)) : -1;

    for (let i = 0; i < fileArr.length; i++) {
      const file = fileArr[i];
      const isVideo = file.type.startsWith("video/");
      const isImage = file.type.startsWith("image/");
      if (!isVideo && !isImage) {
        toast.error(`Pominięto: ${file.name}`);
        continue;
      }
      const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
      const baseName = file.name.replace(/\.[^.]+$/, "");
      const fileName = buildSeoFileName(baseName, ext);
      const { error: uploadError } = await supabase.storage.from("gallery").upload(fileName, file);
      if (uploadError) { toast.error(`Błąd: ${file.name}`); continue; }
      const { data: urlData } = supabase.storage.from("gallery").getPublicUrl(fileName);
      const cleanAlt = baseName.replace(/[-_]+/g, " ").trim();
      const altText = cleanAlt
        ? `Naklejki 3D doming – ${cleanAlt}`
        : `Naklejki 3D doming zalewane żywicą – ${isVideo ? "film produkcyjny" : "zdjęcie realizacji"} nr ${maxPosition + 2 + i}`;
      await supabase.from("gallery_media").insert({
        type: isVideo ? "video" : "image",
        url: urlData.publicUrl,
        alt: altText,
        position: maxPosition + 1 + i,
        mirror: false,
      });
    }
    await fetchMedia();
    setUploading(false);
    toast.success("Pliki dodane do galerii");
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length) uploadFiles(e.dataTransfer.files);
  };

  const handleDelete = async (item: MediaItem) => {
    const fileName = item.url.split("/").pop();
    if (fileName) await supabase.storage.from("gallery").remove([fileName]);
    if (item.poster_url) {
      const posterName = item.poster_url.split("/").pop();
      if (posterName) await supabase.storage.from("gallery").remove([posterName]);
    }
    await supabase.from("gallery_media").delete().eq("id", item.id);
    await fetchMedia();
    toast.success("Usunięto");
  };

  const handleUpdateAlt = async (id: string, alt: string) => {
    await supabase.from("gallery_media").update({ alt }).eq("id", id);
    setMedia((prev) => prev.map((m) => (m.id === id ? { ...m, alt } : m)));
    setEditingAlt(null);
  };

  const handleToggleMirror = async (id: string, mirror: boolean) => {
    await supabase.from("gallery_media").update({ mirror }).eq("id", id);
    setMedia((prev) => prev.map((m) => (m.id === id ? { ...m, mirror } : m)));
  };

  const handlePosterUpload = async (itemId: string, file: File) => {
    const ext = file.name.split(".").pop();
    const fileName = `poster-${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from("gallery").upload(fileName, file);
    if (error) { toast.error("Błąd uploadu postera"); return; }
    const { data: urlData } = supabase.storage.from("gallery").getPublicUrl(fileName);
    await supabase.from("gallery_media").update({ poster_url: urlData.publicUrl }).eq("id", itemId);
    setMedia((prev) => prev.map((m) => (m.id === itemId ? { ...m, poster_url: urlData.publicUrl } : m)));
    toast.success("Poster dodany");
  };

  const moveItem = async (index: number, direction: -1 | 1) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= media.length) return;
    const updated = [...media];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    await Promise.all(updated.map((item, i) =>
      supabase.from("gallery_media").update({ position: i }).eq("id", item.id)
    ));
    setMedia(updated.map((item, i) => ({ ...item, position: i })));
  };

  return (
    <div className="space-y-6">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${
          dragOver ? "border-accent bg-accent/10" : "border-border hover:border-accent/50"
        }`}
      >
        <Upload className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
        <p className="text-foreground font-medium">
          {uploading ? "Przesyłanie..." : "Przeciągnij pliki tutaj lub kliknij"}
        </p>
        <p className="text-muted-foreground text-sm mt-1">JPG, PNG, WEBP, MP4, MOV</p>
        <input ref={fileInputRef} type="file" multiple accept="image/*,video/*" className="hidden"
          onChange={(e) => e.target.files && uploadFiles(e.target.files)} />
      </div>

      <div className="space-y-3">
        <h2 className="text-lg font-semibold text-foreground">Media w galerii ({media.length})</h2>
        {media.length === 0 && (
          <p className="text-muted-foreground text-center py-8">Brak mediów.</p>
        )}
        {media.map((item, index) => (
          <div key={item.id} className="flex items-center gap-4 bg-card border border-border rounded-xl p-4">
            <div className="flex flex-col gap-1">
              <button onClick={() => moveItem(index, -1)} disabled={index === 0} className="text-muted-foreground hover:text-foreground disabled:opacity-30">▲</button>
              <GripVertical className="w-4 h-4 text-muted-foreground" />
              <button onClick={() => moveItem(index, 1)} disabled={index === media.length - 1} className="text-muted-foreground hover:text-foreground disabled:opacity-30">▼</button>
            </div>
            <div className="w-20 h-20 rounded-lg overflow-hidden bg-muted shrink-0">
              {item.type === "image" ? (
                <img src={item.url} alt={item.alt} className="w-full h-full object-cover" />
              ) : (
                <div className="relative w-full h-full">
                  {item.poster_url ? (
                    <img src={item.poster_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <video src={item.url} className="w-full h-full object-cover" muted />
                  )}
                  <Film className="absolute bottom-1 right-1 w-4 h-4 text-primary-foreground drop-shadow" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0 space-y-2">
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-0.5 rounded-full ${item.type === "video" ? "bg-accent/20 text-accent" : "bg-primary/20 text-primary"}`}>
                  {item.type === "video" ? "Film" : "Zdjęcie"}
                </span>
                <span className="text-xs text-muted-foreground">#{index + 1}</span>
              </div>
              {editingAlt === item.id ? (
                <form onSubmit={(e) => { e.preventDefault(); const fd = new FormData(e.currentTarget); handleUpdateAlt(item.id, fd.get("alt") as string); }} className="flex gap-2">
                  <Input name="alt" defaultValue={item.alt} className="h-8 text-sm" autoFocus />
                  <Button type="submit" size="sm" variant="outline" className="h-8">OK</Button>
                  <button type="button" onClick={() => setEditingAlt(null)}><X className="w-4 h-4" /></button>
                </form>
              ) : (
                <p className="text-sm text-foreground truncate cursor-pointer hover:underline" onClick={() => setEditingAlt(item.id)}>
                  {item.alt || "Kliknij aby dodać opis..."}
                </p>
              )}
              <div className="flex items-center gap-4 flex-wrap">
                {item.type === "video" && (
                  <>
                    <div className="flex items-center gap-2">
                      <Switch checked={item.mirror} onCheckedChange={(v) => handleToggleMirror(item.id, v)} />
                      <span className="text-xs text-muted-foreground">Lustrzane</span>
                    </div>
                    <label className="text-xs text-accent cursor-pointer hover:underline">
                      {item.poster_url ? "Zmień poster" : "Dodaj poster"}
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && handlePosterUpload(item.id, e.target.files[0])} />
                    </label>
                  </>
                )}
              </div>
            </div>
            <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive shrink-0" onClick={() => handleDelete(item)}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
